import {
  ONGROUND_LOGO_SURFACE,
  resolveOngroundLogoForSurface,
} from "./ongroundBranding";

const PDF_PAGE_WIDTH = 612;
const PDF_PAGE_HEIGHT = 792;
const PDF_SLOTS_PER_PAGE = 5;

export const MANAGER_PDF_REFERENCE_LAYOUT = Object.freeze({
  page: Object.freeze({
    cssWidth: 620,
    cssHeight: (620 * 11) / 8.5,
    pdfWidth: PDF_PAGE_WIDTH,
    pdfHeight: PDF_PAGE_HEIGHT,
  }),
  header: Object.freeze({
    venueRingLeft: 41,
    venueRingTop: 37.625,
    venueRingSize: 26,
    venueNameLeft: 76,
    venueNameTop: 40.625,
    secureRight: 579,
    secureTop: 35,
    byOngroundTop: 47.75,
    brandLogoLeft: 464.453,
    brandLogoTop: 47.75,
    brandLogoWidth: 114.547,
    brandLogoHeight: 18,
  }),
  main: Object.freeze({
    left: 41,
    width: 394,
    kickerTop: 92.25,
    titleTop: 117.25,
    confirmationTop: 206.25,
    venueTop: 245.844,
    addressTop: 268.844,
    bookingsTop: 314.844,
  }),
  badge: Object.freeze({
    left: 459,
    top: 86.25,
    width: 120,
    height: 140,
    checkLeft: 565,
    checkTop: 78.25,
    checkSize: 22,
    countTop: 92.75,
    bookingsTop: 131.75,
    dateTop: 153.75,
    referenceTop: 174.75,
    referenceLabelTop: 183.75,
    referenceValueTop: 196.75,
    referenceValueHeight: 23,
  }),
  slots: Object.freeze({
    listLeft: 41,
    listWidth: 394,
    groupHeaderHeight: 51,
    dateColumnWidth: 52,
    columnGap: 12,
    dateBoxSize: 40,
    dateBoxRadius: 9,
    slotsLeftOffset: 64,
    slotsBottomPadding: 0,
    rowHeight: 34,
    lastRowHeight: 34,
    timeFontSize: 11,
    timeFontWeight: 550,
    priceFontSize: 10,
    priceFontWeight: 750,
  }),
  policy: Object.freeze({
    top: 602.344,
    headingHeight: 30,
    contentTop: 632.344,
    contentHeight: 116,
    pleaseNoteLeft: 41,
    pleaseNoteTop: 660.344,
    textLeft: 41,
    textTop: 678.344,
    textWidth: 538,
    footerTop: 748.344,
    footerHeight: 53,
  }),
});

const toPdfSafeText = (value) =>
  String(value == null ? "" : value)
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201c\u201d]/g, '"')
    .replace(/[\u2013\u2014]/g, "-")
    .replace(/\u00b7/g, " - ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/[^\x20-\x7E]/g, "");

const escapePdfText = (value) =>
  toPdfSafeText(value)
    .replace(/\\/g, "\\\\")
    .replace(/\(/g, "\\(")
    .replace(/\)/g, "\\)");

const wrapText = (value, maxCharacters = 64) => {
  const text = toPdfSafeText(value);
  if (!text) return [];

  const words = text.split(/\s+/);
  const lines = [];
  let current = "";

  words.forEach((word) => {
    const candidate = current ? `${current} ${word}` : word;

    if (candidate.length <= maxCharacters || !current) {
      current = candidate;
      return;
    }

    lines.push(current);
    current = word;
  });

  if (current) lines.push(current);
  return lines;
};

const encode = (value) =>
  Uint8Array.from(
    String(value),
    (character) => character.charCodeAt(0),
  );

const textCommand = ({
  text,
  x,
  y,
  size = 11,
  font = "F1",
  color = "0.09 0.14 0.12",
}) => {
  const safeText = escapePdfText(text);
  if (!safeText) return "";

  return [
    "BT",
    `/${font} ${size} Tf`,
    `${color} rg`,
    `1 0 0 1 ${x} ${y} Tm`,
    `(${safeText}) Tj`,
    "ET",
  ].join("\n");
};

const trackedTextCommand = ({
  text,
  x,
  y,
  size = 7,
  font = "F1",
  color = "0.09 0.14 0.12",
  charSpacing = 0,
}) => {
  const safeText = escapePdfText(text);
  if (!safeText) return "";

  return [
    "BT",
    `/${font} ${size} Tf`,
    `${charSpacing} Tc`,
    `${color} rg`,
    `1 0 0 1 ${x} ${y} Tm`,
    `(${safeText}) Tj`,
    "ET",
  ].join("\n");
};

const approximatePdfTextWidth = ({
  text,
  size,
  font = "F1",
  charSpacing = 0,
}) => {
  const safeText = toPdfSafeText(text);
  if (!safeText) return 0;

  const widthFactor =
    font === "F3" ? 0.6 : font === "F2" ? 0.54 : 0.5;

  return (
    safeText.length * size * widthFactor +
    Math.max(0, safeText.length - 1) * charSpacing
  );
};

const centeredPdfTextCommand = ({
  text,
  centerX = PDF_PAGE_WIDTH / 2,
  y,
  size = 7,
  font = "F1",
  color = "0.09 0.14 0.12",
  charSpacing = 0,
}) => {
  const width = approximatePdfTextWidth({
    text,
    size,
    font,
    charSpacing,
  });

  return trackedTextCommand({
    text,
    x: centerX - width / 2,
    y,
    size,
    font,
    color,
    charSpacing,
  });
};

const rectCommand = ({
  x,
  y,
  width,
  height,
  fill = "",
  stroke = "",
  lineWidth = 0.8,
}) =>
  [
    "q",
    fill ? `${fill} rg` : "",
    stroke ? `${stroke} RG` : "",
    stroke ? `${lineWidth} w` : "",
    `${x} ${y} ${width} ${height} re`,
    fill && stroke ? "B" : fill ? "f" : "S",
    "Q",
  ]
    .filter(Boolean)
    .join("\n");

const lineCommand = ({
  x1,
  y1,
  x2,
  y2,
  color = "0.86 0.89 0.87",
  width = 0.8,
  dash = "",
}) =>
  [
    "q",
    `${color} RG`,
    `${width} w`,
    dash ? `[${dash}] 0 d` : "",
    `${x1} ${y1} m`,
    `${x2} ${y2} l`,
    "S",
    "Q",
  ]
    .filter(Boolean)
    .join("\n");

const circleCommand = ({
  cx,
  cy,
  radius,
  fill = "",
  stroke = "",
  lineWidth = 0.8,
}) => {
  const k = 0.5522847498;
  const c = radius * k;

  return [
    "q",
    fill ? `${fill} rg` : "",
    stroke ? `${stroke} RG` : "",
    stroke ? `${lineWidth} w` : "",
    `${cx + radius} ${cy} m`,
    `${cx + radius} ${cy + c} ${cx + c} ${cy + radius} ${cx} ${cy + radius} c`,
    `${cx - c} ${cy + radius} ${cx - radius} ${cy + c} ${cx - radius} ${cy} c`,
    `${cx - radius} ${cy - c} ${cx - c} ${cy - radius} ${cx} ${cy - radius} c`,
    `${cx + c} ${cy - radius} ${cx + radius} ${cy - c} ${cx + radius} ${cy} c`,
    fill && stroke ? "B" : fill ? "f" : "S",
    "Q",
  ]
    .filter(Boolean)
    .join("\n");
};

const imageCommand = ({ name, x, y, width, height }) =>
  [
    "q",
    `${width} 0 0 ${height} ${x} ${y} cm`,
    `/${name} Do`,
    "Q",
  ].join("\n");

const bytesToBinaryString = (bytes) => {
  const source =
    bytes instanceof Uint8Array
      ? bytes
      : Uint8Array.from(bytes || []);
  let output = "";

  for (
    let offset = 0;
    offset < source.length;
    offset += 0x8000
  ) {
    output += String.fromCharCode(
      ...source.subarray(offset, offset + 0x8000),
    );
  }

  return output;
};

const dataUrlToBytes = (dataUrl) => {
  const base64 =
    String(dataUrl || "").split(",")[1] || "";

  if (
    !base64 ||
    typeof window === "undefined" ||
    typeof window.atob !== "function"
  ) {
    return null;
  }

  const binary = window.atob(base64);
  return Uint8Array.from(
    binary,
    (character) => character.charCodeAt(0),
  );
};

const hexToRgb = (value, fallback) => {
  const source = String(value || "").trim();
  const normalized = /^#[0-9a-fA-F]{6}$/.test(source)
    ? source.slice(1)
    : /^#[0-9a-fA-F]{3}$/.test(source)
      ? source
          .slice(1)
          .split("")
          .map((char) => `${char}${char}`)
          .join("")
      : fallback.replace("#", "");

  return [
    parseInt(normalized.slice(0, 2), 16) / 255,
    parseInt(normalized.slice(2, 4), 16) / 255,
    parseInt(normalized.slice(4, 6), 16) / 255,
  ];
};

const mixRgb = (source, target, sourceWeight) =>
  source.map(
    (component, index) =>
      component * sourceWeight +
      target[index] * (1 - sourceWeight),
  );

const pdfColor = (rgb) =>
  rgb
    .map((component) =>
      Math.max(0, Math.min(1, component)).toFixed(4),
    )
    .join(" ");

const cleanNumber = (value, fallback = null) => {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return fallback;
  }

  const number = Number(value);
  return Number.isFinite(number)
    ? number
    : fallback;
};

const formatMoney = (currency, amount) => {
  const number = cleanNumber(amount, null);
  if (number === null) return "";

  const code =
    toPdfSafeText(currency).toUpperCase() || "INR";

  return `${code} ${new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
  }).format(number)}`;
};

const parseYmd = (value) => {
  const match = String(value || "").match(
    /^(\d{4})-(\d{2})-(\d{2})$/,
  );

  if (!match) return null;

  const [, year, month, day] = match;
  const date = new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    12,
    0,
    0,
    0,
  );

  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDate = (value) => {
  const date = parseYmd(value);
  if (!date) return toPdfSafeText(value);

  const weekdays = [
    "Sun",
    "Mon",
    "Tue",
    "Wed",
    "Thu",
    "Fri",
    "Sat",
  ];
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  return `${weekdays[date.getDay()]}, ${date.getDate()} ${
    months[date.getMonth()]
  } ${date.getFullYear()}`;
};

const formatDay = (value) => {
  const date = parseYmd(value);
  return date ? String(date.getDate()) : "";
};

const formatMonth = (value) => {
  const date = parseYmd(value);
  if (!date) return "";

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
  }).format(date);
};

const formatTime = (value) => {
  const match = String(value || "").match(
    /^(\d{1,2}):(\d{2})$/,
  );

  if (!match) return toPdfSafeText(value);

  const hour = Number(match[1]);
  const minute = match[2];

  if (
    !Number.isInteger(hour) ||
    hour < 0 ||
    hour > 23
  ) {
    return toPdfSafeText(value);
  }

  const period = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 || 12;

  return `${displayHour}:${minute} ${period}`;
};

const normalizeSlots = (slots) =>
  Array.isArray(slots)
    ? slots
        .slice(0, 48)
        .map((slot) => ({
          date: String(slot?.date || "").trim(),
          start: String(slot?.start || "").trim(),
          end: String(slot?.end || "").trim(),
          price: cleanNumber(
            slot?.price ??
              slot?.amount ??
              slot?.totalAmount,
            null,
          ),
          currency: String(
            slot?.currency || "",
          )
            .trim()
            .toUpperCase(),
        }))
        .filter(
          (slot) =>
            slot.date &&
            slot.start &&
            slot.end,
        )
    : [];

const formatDateRange = (slots) => {
  const dates = Array.from(
    new Set(
      (slots || [])
        .map((slot) => slot.date)
        .filter(Boolean),
    ),
  ).sort();

  if (!dates.length) return "";

  const first = parseYmd(dates[0]);
  const last = parseYmd(dates[dates.length - 1]);

  if (!first || !last) return dates[0];

  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const firstLabel = `${months[first.getMonth()]} ${first.getDate()}`;

  if (dates.length === 1) return firstLabel;

  const sameMonth =
    first.getFullYear() === last.getFullYear() &&
    first.getMonth() === last.getMonth();

  const lastLabel = sameMonth
    ? String(last.getDate())
    : `${months[last.getMonth()]} ${last.getDate()}`;

  return `${firstLabel} - ${lastLabel}`;
};

const chunk = (items, size) => {
  const result = [];

  for (let index = 0; index < items.length; index += size) {
    result.push(items.slice(index, index + size));
  }

  return result;
};

const buildPageContent = ({
  pageIndex,
  pageCount,
  pageSlots,
  allSlots,
  slotOffset,
  bookingReference,
  venueName,
  groundName,
  bookingWhen,
  venueAddress,
  createdAt,
  currency,
  subtotal,
  discountAmount,
  finalAmount,
  promoCode,
  cancellationPolicy,
  supportEmail,
  supportPhone,
  primaryColor,
  accentColor,
  hasLogoImage,
  logoImage,
  hasPricingEvidence,
}) => {
  const content = [];

  const primary = hexToRgb(primaryColor, "#0E5B41");
  const accent = hexToRgb(accentColor, "#16241E");
  const primaryDeep = mixRgb(primary, [0, 0, 0], 0.78);
  const primaryTint = mixRgb(primary, [1, 1, 1], 0.12);
  const primarySurface = mixRgb(primary, [1, 1, 1], 0.07);
  const primaryBorder = mixRgb(primary, [1, 1, 1], 0.28);
  const line = hexToRgb("#DCE3DE", "#DCE3DE");
  const muted = hexToRgb("#8B978F", "#8B978F");
  const inkSoft = hexToRgb("#4C5A52", "#4C5A52");
  const gold = hexToRgb("#8C6220", "#8C6220");

  const primaryPdf = pdfColor(primary);
  const primaryDeepPdf = pdfColor(primaryDeep);
  const primaryTintPdf = pdfColor(primaryTint);
  const primarySurfacePdf = pdfColor(primarySurface);
  const primaryBorderPdf = pdfColor(primaryBorder);
  const accentPdf = pdfColor(accent);
  const linePdf = pdfColor(line);
  const mutedPdf = pdfColor(muted);
  const inkSoftPdf = pdfColor(inkSoft);
  const goldPdf = pdfColor(gold);

  for (let x = 7; x < PDF_PAGE_WIDTH; x += 16) {
    content.push(
      circleCommand({
        cx: x,
        cy: 788,
        radius: 2.6,
        fill: "0.9569 0.9569 0.9647",
      }),
    );
  }

  content.push(
    circleCommand({
      cx: 55,
      cy: 744,
      radius: 13,
      stroke: accentPdf,
      lineWidth: 1.1,
    }),
    textCommand({
      text: String(venueName || "O").charAt(0).toUpperCase(),
      x: 51.5,
      y: 739,
      size: 11,
      font: "F2",
      color: accentPdf,
    }),
    textCommand({
      text: venueName || "Onground venue",
      x: 77,
      y: 739,
      size: 16,
      font: "F2",
      color: accentPdf,
    }),
    textCommand({
      text: "SECURE BOOKING",
      x: 468,
      y: 750,
      size: 7.5,
      font: "F3",
      color: goldPdf,
    }),
  );

  if (hasLogoImage) {
    const aspect =
      Number(logoImage.width) /
      Math.max(Number(logoImage.height), 1);
    const logoWidth = 96;
    const logoHeight =
      logoWidth / Math.max(aspect, 0.1);

    content.push(
      imageCommand({
        name: "BrandLogo",
        x: 466,
        y: 719,
        width: logoWidth,
        height: Math.min(24, logoHeight),
      }),
    );
  } else {
    content.push(
      textCommand({
        text: "BY ONGROUND",
        x: 467,
        y: 731,
        size: 12,
        font: "F2",
        color: accentPdf,
      }),
    );
  }

  content.push(
    textCommand({
      text: "BOOKING CONFIRMED",
      x: 40,
      y: 687,
      size: 8,
      font: "F3",
      color: primaryDeepPdf,
    }),
    circleCommand({
      cx: 34,
      cy: 690,
      radius: 3,
      fill: primaryPdf,
    }),
    textCommand({
      text:
        pageIndex === 0
          ? "Booking"
          : "Booking Confirmation",
      x: 40,
      y: 650,
      size: pageIndex === 0 ? 28 : 22,
      font: "F2",
      color: accentPdf,
    }),
  );

  if (pageIndex === 0) {
    content.push(
      textCommand({
        text: "Confirmation",
        x: 40,
        y: 619,
        size: 28,
        font: "F2",
        color: accentPdf,
      }),
      textCommand({
        text: "Your payment was verified and your booking is confirmed.",
        x: 40,
        y: 590,
        size: 9.5,
        color: inkSoftPdf,
      }),
    );

    if (createdAt) {
      content.push(
        textCommand({
          text: "CREATED AT",
          x: 40,
          y: 572,
          size: 6.5,
          font: "F3",
          color: mutedPdf,
        }),
        textCommand({
          text: createdAt,
          x: 94,
          y: 572,
          size: 8,
          font: "F2",
          color: accentPdf,
        }),
      );
    }
  } else {
    content.push(
      textCommand({
        text: "Continued",
        x: 40,
        y: 624,
        size: 10,
        font: "F3",
        color: mutedPdf,
      }),
    );
  }

  const venueLine = [groundName, venueName]
    .filter(Boolean)
    .join(" - ");

  content.push(
    textCommand({
      text: venueLine,
      x: 40,
      y: 555,
      size: 14,
      font: "F2",
      color: accentPdf,
    }),
  );

  wrapText(venueAddress, 62)
    .slice(0, 2)
    .forEach((lineText, index) => {
      content.push(
        textCommand({
          text: lineText,
          x: 40,
          y: 536 - index * 13,
          size: 8.5,
          color: mutedPdf,
        }),
      );
    });

  const badgeX = 452;
  const badgeY = 547;
  const badgeWidth = 120;
  const badgeHeight = 140;

  content.push(
    rectCommand({
      x: badgeX,
      y: badgeY,
      width: badgeWidth,
      height: badgeHeight,
      fill: "1 1 1",
      stroke: linePdf,
      lineWidth: 1.2,
    }),
    circleCommand({
      cx: badgeX + badgeWidth - 2,
      cy: badgeY + badgeHeight + 2,
      radius: 11,
      fill: primaryPdf,
    }),
    lineCommand({
      x1: badgeX + badgeWidth - 7,
      y1: badgeY + badgeHeight + 1,
      x2: badgeX + badgeWidth - 3,
      y2: badgeY + badgeHeight - 3,
      color: "1 1 1",
      width: 1.6,
    }),
    lineCommand({
      x1: badgeX + badgeWidth - 3,
      y1: badgeY + badgeHeight - 3,
      x2: badgeX + badgeWidth + 4,
      y2: badgeY + badgeHeight + 5,
      color: "1 1 1",
      width: 1.6,
    }),
    textCommand({
      text: String(allSlots.length || 1),
      x: badgeX + 47,
      y: badgeY + 92,
      size: 30,
      font: "F2",
      color: accentPdf,
    }),
    textCommand({
      text: "Bookings",
      x: badgeX + 38,
      y: badgeY + 72,
      size: 9,
      color: inkSoftPdf,
    }),
    textCommand({
      text: formatDateRange(allSlots) || "Confirmed",
      x: badgeX + 29,
      y: badgeY + 54,
      size: 7.5,
      color: mutedPdf,
    }),
    lineCommand({
      x1: badgeX + 9,
      y1: badgeY + 42,
      x2: badgeX + badgeWidth - 9,
      y2: badgeY + 42,
      color: linePdf,
      dash: "2 2",
    }),
    textCommand({
      text: "REFERENCE NO",
      x: badgeX + 27,
      y: badgeY + 28,
      size: 6.5,
      font: "F3",
      color: mutedPdf,
    }),
    rectCommand({
      x: badgeX + 15,
      y: badgeY + 7,
      width: badgeWidth - 30,
      height: 17,
      fill: primaryTintPdf,
      stroke: primaryTintPdf,
    }),
    textCommand({
      text: bookingReference || "-",
      x: badgeX + 21,
      y: badgeY + 12,
      size: 7.5,
      font: "F3",
      color: primaryDeepPdf,
    }),
  );

  let y = 482;

  if (!pageSlots.length) {
    wrapText(bookingWhen || "Confirmed", 62)
      .slice(0, 3)
      .forEach((lineText, index) => {
        content.push(
          textCommand({
            text: lineText,
            x: 40,
            y: y - index * 15,
            size: 10,
            font: index === 0 ? "F2" : "F1",
            color: accentPdf,
          }),
        );
      });
  } else {
    pageSlots.forEach((slot, index) => {
      const price = formatMoney(
        slot.currency,
        slot.price,
      );
      const isFirstForDate =
        index === 0 ||
        pageSlots[index - 1]?.date !== slot.date;

      if (isFirstForDate) {
        content.push(
          rectCommand({
            x: 40,
            y: y - 27,
            width: 40,
            height: 40,
            fill: primaryTintPdf,
          }),
          textCommand({
            text: formatDay(slot.date),
            x: 53,
            y: y - 9,
            size: 12,
            font: "F2",
            color: accentPdf,
          }),
          textCommand({
            text: formatMonth(slot.date),
            x: 51,
            y: y - 22,
            size: 6.5,
            color: accentPdf,
          }),
          textCommand({
            text: formatDate(slot.date),
            x: 94,
            y,
            size: 9.2,
            font: "F2",
            color: accentPdf,
          }),
        );
      }

      content.push(
        textCommand({
          text: `${formatTime(slot.start)} - ${formatTime(slot.end)}`,
          x: 94,
          y: isFirstForDate ? y - 17 : y - 4,
          size: 8.4,
          color: mutedPdf,
        }),
      );

      if (price) {
        content.push(
          textCommand({
            text: price,
            x: 472,
            y: y - 8,
            size: 8.8,
            font: "F2",
            color: accentPdf,
          }),
        );
      }

      content.push(
        lineCommand({
          x1: 94,
          y1: y - 34,
          x2: 430,
          y2: y - 34,
          color: linePdf,
        }),
      );

      y -= 48;
    });
  }

  if (pageIndex === pageCount - 1 && hasPricingEvidence) {
    const summaryTop = Math.min(y - 6, 258);
    const summaryHeight =
      cleanNumber(discountAmount, 0) > 0 ? 84 : 66;
    const summaryY = summaryTop - summaryHeight;

    content.push(
      rectCommand({
        x: 40,
        y: summaryY,
        width: 520,
        height: summaryHeight,
        fill: primarySurfacePdf,
        stroke: primaryBorderPdf,
        lineWidth: 0.8,
      }),
      textCommand({
        text: "PAYMENT SUMMARY",
        x: 54,
        y: summaryTop - 15,
        size: 6.5,
        font: "F3",
        color: mutedPdf,
      }),
      textCommand({
        text: "Sub Total",
        x: 54,
        y: summaryTop - 34,
        size: 8.5,
        color: mutedPdf,
      }),
      textCommand({
        text: formatMoney(currency, subtotal),
        x: 474,
        y: summaryTop - 34,
        size: 8.8,
        font: "F2",
        color: accentPdf,
      }),
    );

    let totalBaseline = summaryTop - 53;

    if (cleanNumber(discountAmount, 0) > 0) {
      content.push(
        textCommand({
          text: promoCode
            ? `Discount (${promoCode})`
            : "Discount",
          x: 54,
          y: summaryTop - 52,
          size: 8.2,
          color: mutedPdf,
        }),
        textCommand({
          text: `- ${formatMoney(currency, discountAmount)}`,
          x: 474,
          y: summaryTop - 52,
          size: 8.5,
          font: "F2",
          color: accentPdf,
        }),
      );
      totalBaseline = summaryTop - 71;
    }

    content.push(
      textCommand({
        text: "TOTAL PAID",
        x: 54,
        y: totalBaseline,
        size: 9.2,
        font: "F2",
        color: accentPdf,
      }),
      textCommand({
        text: formatMoney(currency, finalAmount),
        x: 464,
        y: totalBaseline,
        size: 10,
        font: "F2",
        color: accentPdf,
      }),
    );

    y = summaryY - 14;
  }

  if (pageIndex === pageCount - 1 && cancellationPolicy) {
    /*
     * Manager PDF reference:
     * - full-width divider
     * - centered uppercase "CANCELLATION POLICY" with a small dot
     * - separate PLEASE NOTE label
     * - written dynamic venue policy
     * - centered dynamic support sentence at the bottom
     *
     * Confirmation-page behavior is intentionally separate and remains popup-only.
     */
    const policyDividerY = Math.min(y - 4, 154);
    const policyHeading = "CANCELLATION POLICY";
    const policyHeadingSize = 7.125;
    const policyHeadingTracking = 0.7125;
    const policyHeadingWidth = approximatePdfTextWidth({
      text: policyHeading,
      size: policyHeadingSize,
      font: "F3",
      charSpacing: policyHeadingTracking,
    });
    const policyDotGap = 5;
    const policyDotRadius = 1.15;
    const policyGroupWidth =
      policyHeadingWidth + policyDotGap + policyDotRadius * 2;
    const policyHeadingX =
      PDF_PAGE_WIDTH / 2 - policyGroupWidth / 2;
    const policyHeadingY = policyDividerY - 17;

    content.push(
      rectCommand({
        x: 0,
        y: 0,
        width: PDF_PAGE_WIDTH,
        height: policyDividerY,
        fill: "1 1 1",
      }),
      lineCommand({
        x1: 0,
        y1: policyDividerY,
        x2: PDF_PAGE_WIDTH,
        y2: policyDividerY,
        color: linePdf,
        width: 0.8,
      }),
      trackedTextCommand({
        text: policyHeading,
        x: policyHeadingX,
        y: policyHeadingY,
        size: policyHeadingSize,
        font: "F3",
        color: mutedPdf,
        charSpacing: policyHeadingTracking,
      }),
      circleCommand({
        cx:
          policyHeadingX +
          policyHeadingWidth +
          policyDotGap +
          policyDotRadius,
        cy: policyHeadingY + 2.4,
        radius: policyDotRadius,
        fill: mutedPdf,
      }),
      trackedTextCommand({
        text: "PLEASE NOTE",
        x: 40,
        y: policyDividerY - 53,
        size: 7.5,
        color: mutedPdf,
        charSpacing: 0.6,
      }),
    );

    wrapText(cancellationPolicy, 92)
      .slice(0, 4)
      .forEach((lineText, index) => {
        content.push(
          textCommand({
            text: lineText,
            x: 40,
            y: policyDividerY - 72 - index * 15,
            size: 9.2,
            color: mutedPdf,
          }),
        );
      });
  }

  if (
    pageIndex === pageCount - 1 &&
    (supportEmail || supportPhone)
  ) {
    const supportParts = [
      "If you need more information, contact us:",
      supportEmail || "",
      supportPhone
        ? `${supportEmail ? "or " : ""}phone ${supportPhone}`
        : "",
    ].filter(Boolean);

    const supportLine = supportParts.join(" ");

    wrapText(supportLine, 104)
      .slice(0, 2)
      .forEach((lineText, index) => {
        content.push(
          centeredPdfTextCommand({
            text: lineText,
            y: 24 - index * 10,
            size: 7.5,
            color: mutedPdf,
          }),
        );
      });
  }

  return `${content.filter(Boolean).join("\n")}\n`;
};

export const buildBookingConfirmationPdf = ({
  bookingReference,
  venueName,
  groundName,
  bookingWhen,
  venueAddress,
  createdAt = "",
  slots = [],
  currency = "INR",
  subtotal = null,
  discountAmount = null,
  finalAmount = null,
  promoCode = "",
  cancellationPolicy = "",
  supportEmail = "",
  supportPhone = "",
  primaryColor = "#0E5B41",
  accentColor = "#16241E",
  logoImage = null,
}) => {
  const normalizedSlots = normalizeSlots(slots);
  const hasPricingEvidence = Boolean(
    cleanNumber(subtotal, null) !== null ||
      cleanNumber(discountAmount, null) !== null ||
      cleanNumber(finalAmount, null) !== null ||
      normalizedSlots.some(
        (slot) => cleanNumber(slot.price, null) !== null,
      ),
  );

  const resolvedSubtotal = hasPricingEvidence
    ? cleanNumber(subtotal, null) ??
      normalizedSlots.reduce(
        (sum, slot) =>
          sum + (cleanNumber(slot.price, 0) || 0),
        0,
      )
    : null;

  const resolvedDiscount = hasPricingEvidence
    ? Math.max(0, cleanNumber(discountAmount, 0) || 0)
    : null;

  const resolvedTotal = hasPricingEvidence
    ? Math.max(
        0,
        cleanNumber(finalAmount, null) ??
          Math.max(
            0,
            (resolvedSubtotal || 0) -
              (resolvedDiscount || 0),
          ),
      )
    : null;

  const hasLogoImage = Boolean(
    logoImage?.bytes?.length &&
      Number.isFinite(Number(logoImage?.width)) &&
      Number.isFinite(Number(logoImage?.height)) &&
      Number(logoImage.width) > 0 &&
      Number(logoImage.height) > 0,
  );

  const slotPages = normalizedSlots.length
    ? chunk(normalizedSlots, PDF_SLOTS_PER_PAGE)
    : [[]];

  const pageCount = slotPages.length;
  const pageStreams = slotPages.map(
    (pageSlots, pageIndex) =>
      buildPageContent({
        pageIndex,
        pageCount,
        pageSlots,
        allSlots: normalizedSlots,
        slotOffset: pageIndex * PDF_SLOTS_PER_PAGE,
        bookingReference,
        venueName,
        groundName,
        bookingWhen,
        venueAddress,
        createdAt,
        currency,
        subtotal: resolvedSubtotal,
        discountAmount: resolvedDiscount,
        finalAmount: resolvedTotal,
        promoCode,
        cancellationPolicy,
        supportEmail,
        supportPhone,
        primaryColor,
        accentColor,
        hasLogoImage,
        logoImage,
        hasPricingEvidence,
      }),
  );

  const catalogId = 1;
  const pagesId = 2;
  const firstPageId = 3;
  const firstContentId = firstPageId + pageCount;
  const fontRegularId = firstContentId + pageCount;
  const fontDisplayId = fontRegularId + 1;
  const fontMonoId = fontRegularId + 2;
  const logoId = hasLogoImage ? fontRegularId + 3 : null;
  const objectCount = fontMonoId + (hasLogoImage ? 1 : 0);

  const objects = new Array(objectCount).fill("");

  objects[catalogId - 1] =
    `<< /Type /Catalog /Pages ${pagesId} 0 R >>`;

  const pageIds = Array.from(
    { length: pageCount },
    (_, index) => firstPageId + index,
  );

  objects[pagesId - 1] =
    `<< /Type /Pages /Kids [${pageIds
      .map((id) => `${id} 0 R`)
      .join(" ")}] /Count ${pageCount} >>`;

  pageStreams.forEach((stream, pageIndex) => {
    const pageId = firstPageId + pageIndex;
    const contentId = firstContentId + pageIndex;

    const resources = hasLogoImage
      ? `<< /Font << /F1 ${fontRegularId} 0 R /F2 ${fontDisplayId} 0 R /F3 ${fontMonoId} 0 R >> /XObject << /BrandLogo ${logoId} 0 R >> >>`
      : `<< /Font << /F1 ${fontRegularId} 0 R /F2 ${fontDisplayId} 0 R /F3 ${fontMonoId} 0 R >> >>`;

    objects[pageId - 1] =
      `<< /Type /Page /Parent ${pagesId} 0 R /MediaBox [0 0 ${PDF_PAGE_WIDTH} ${PDF_PAGE_HEIGHT}] /Resources ${resources} /Contents ${contentId} 0 R >>`;

    objects[contentId - 1] =
      `<< /Length ${encode(stream).length} >>\nstream\n${stream}endstream`;
  });

  objects[fontRegularId - 1] =
    "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>";
  objects[fontDisplayId - 1] =
    "<< /Type /Font /Subtype /Type1 /BaseFont /Times-Bold >>";
  objects[fontMonoId - 1] =
    "<< /Type /Font /Subtype /Type1 /BaseFont /Courier-Bold >>";

  if (hasLogoImage && logoId) {
    const logoBytes =
      logoImage.bytes instanceof Uint8Array
        ? logoImage.bytes
        : Uint8Array.from(logoImage.bytes);

    objects[logoId - 1] =
      `<< /Type /XObject /Subtype /Image /Width ${Math.round(
        Number(logoImage.width),
      )} /Height ${Math.round(
        Number(logoImage.height),
      )} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${
        logoBytes.length
      } >>\nstream\n${bytesToBinaryString(
        logoBytes,
      )}\nendstream`;
  }

  let pdf =
    "%PDF-1.4\n% OnGround booking confirmation\n";
  const offsets = [0];

  objects.forEach((object, index) => {
    offsets[index + 1] = encode(pdf).length;
    pdf += `${index + 1} 0 obj\n${object}\nendobj\n`;
  });

  const xrefOffset = encode(pdf).length;
  pdf += `xref\n0 ${objects.length + 1}\n`;
  pdf += "0000000000 65535 f \n";

  offsets.slice(1).forEach((offset) => {
    pdf += `${String(offset)
      .padStart(10, "0")} 00000 n \n`;
  });

  pdf += [
    "trailer",
    `<< /Size ${objects.length + 1} /Root ${catalogId} 0 R >>`,
    "startxref",
    String(xrefOffset),
    "%%EOF",
    "",
  ].join("\n");

  return encode(pdf);
};


const MANAGER_PDF_COLORS = Object.freeze({
  ink: "#16241E",
  inkSoft: "#4C5A52",
  pine: "#0E5B41",
  pineDeep: "#0A4531",
  pineTint: "#E3F1E9",
  goldDeep: "#8C6220",
  muted: "#8B978F",
  line: "#DCE3DE",
  rowLine: "#EDF0EE",
  groupLine: "#E3E8E5",
  perforation: "#EEF2EF",
  white: "#FFFFFF",
});

const MANAGER_PDF_FONTS = Object.freeze({
  display: '"Fraunces", serif',
  body: '"Inter", sans-serif',
  mono: '"IBM Plex Mono", monospace',
});

const managerRoundedRectPath = (context, x, y, width, height, radius) => {
  const safeRadius = Math.max(0, Math.min(radius, width / 2, height / 2));

  context.beginPath();
  context.moveTo(x + safeRadius, y);
  context.lineTo(x + width - safeRadius, y);
  context.quadraticCurveTo(x + width, y, x + width, y + safeRadius);
  context.lineTo(x + width, y + height - safeRadius);
  context.quadraticCurveTo(
    x + width,
    y + height,
    x + width - safeRadius,
    y + height,
  );
  context.lineTo(x + safeRadius, y + height);
  context.quadraticCurveTo(x, y + height, x, y + height - safeRadius);
  context.lineTo(x, y + safeRadius);
  context.quadraticCurveTo(x, y, x + safeRadius, y);
  context.closePath();
};

const managerSetCanvasFont = (
  context,
  { family = MANAGER_PDF_FONTS.body, size = 12, weight = 400 } = {},
) => {
  context.font = `${weight} ${size}px ${family}`;
  context.textBaseline = "top";
};

const managerDrawText = (
  context,
  text,
  {
    x,
    y,
    family = MANAGER_PDF_FONTS.body,
    size = 12,
    weight = 400,
    color = MANAGER_PDF_COLORS.ink,
    align = "left",
  },
) => {
  const value = String(text == null ? "" : text);
  if (!value) return;

  context.save();
  managerSetCanvasFont(context, { family, size, weight });
  context.fillStyle = color;
  context.textAlign = align;
  context.fillText(value, x, y);
  context.restore();
};

const managerMeasureTrackedText = (
  context,
  text,
  { family, size, weight, letterSpacing = 0 },
) => {
  const value = String(text == null ? "" : text);
  if (!value) return 0;

  context.save();
  managerSetCanvasFont(context, { family, size, weight });
  let width = 0;
  [...value].forEach((character, index) => {
    width += context.measureText(character).width;
    if (index < value.length - 1) width += letterSpacing;
  });
  context.restore();
  return width;
};

const managerDrawTrackedText = (
  context,
  text,
  {
    x,
    y,
    family = MANAGER_PDF_FONTS.body,
    size = 12,
    weight = 400,
    color = MANAGER_PDF_COLORS.ink,
    letterSpacing = 0,
    align = "left",
  },
) => {
  const value = String(text == null ? "" : text);
  if (!value) return;

  const width = managerMeasureTrackedText(context, value, {
    family,
    size,
    weight,
    letterSpacing,
  });
  let cursor =
    align === "center"
      ? x - width / 2
      : align === "right"
        ? x - width
        : x;

  context.save();
  managerSetCanvasFont(context, { family, size, weight });
  context.fillStyle = color;
  context.textAlign = "left";

  [...value].forEach((character, index) => {
    context.fillText(character, cursor, y);
    cursor += context.measureText(character).width;
    if (index < value.length - 1) cursor += letterSpacing;
  });

  context.restore();
};

const managerWrapCanvasText = (
  context,
  value,
  { maxWidth, family, size, weight },
) => {
  const source = String(value == null ? "" : value).trim();
  if (!source) return [];

  context.save();
  managerSetCanvasFont(context, { family, size, weight });

  const words = source.split(/\s+/);
  const lines = [];
  let current = "";

  words.forEach((word) => {
    const candidate = current ? `${current} ${word}` : word;

    if (!current || context.measureText(candidate).width <= maxWidth) {
      current = candidate;
      return;
    }

    lines.push(current);
    current = word;
  });

  if (current) lines.push(current);
  context.restore();
  return lines;
};

const managerDrawWrappedText = (
  context,
  value,
  {
    x,
    y,
    maxWidth,
    maxLines = 2,
    lineHeight,
    family = MANAGER_PDF_FONTS.body,
    size = 12,
    weight = 400,
    color = MANAGER_PDF_COLORS.ink,
    align = "left",
  },
) => {
  const lines = managerWrapCanvasText(context, value, {
    maxWidth,
    family,
    size,
    weight,
  }).slice(0, maxLines);

  lines.forEach((line, index) => {
    managerDrawText(context, line, {
      x,
      y: y + index * lineHeight,
      family,
      size,
      weight,
      color,
      align,
    });
  });

  return lines.length;
};

const managerGroupSlotsByDate = (slots) => {
  const groups = [];
  const byDate = new Map();

  (slots || []).forEach((slot) => {
    let group = byDate.get(slot.date);
    if (!group) {
      group = { date: slot.date, slots: [] };
      byDate.set(slot.date, group);
      groups.push(group);
    }
    group.slots.push(slot);
  });

  return groups;
};

const managerSlotRowsHeight = (slotCount) => {
  const count = Math.max(0, Number(slotCount) || 0);
  if (!count) return 0;

  const layout = MANAGER_PDF_REFERENCE_LAYOUT.slots;

  return (
    Math.max(0, count - 1) * layout.rowHeight +
    layout.lastRowHeight
  );
};

const managerGroupHeight = (slotCount) =>
  MANAGER_PDF_REFERENCE_LAYOUT.slots.groupHeaderHeight +
  managerSlotRowsHeight(slotCount) +
  MANAGER_PDF_REFERENCE_LAYOUT.slots.slotsBottomPadding;

const managerPaginateSlotGroups = (slots) => {
  const maxHeight =
    MANAGER_PDF_REFERENCE_LAYOUT.policy.top -
    MANAGER_PDF_REFERENCE_LAYOUT.main.bookingsTop -
    10;
  const sourceGroups = managerGroupSlotsByDate(slots);
  if (!sourceGroups.length) return [[]];

  const pages = [];
  let page = [];
  let usedHeight = 0;

  const pushPage = () => {
    if (page.length) pages.push(page);
    page = [];
    usedHeight = 0;
  };

  sourceGroups.forEach((sourceGroup) => {
    let offset = 0;

    while (offset < sourceGroup.slots.length) {
      const remainingHeight = maxHeight - usedHeight;
      const fixedHeight =
        MANAGER_PDF_REFERENCE_LAYOUT.slots.groupHeaderHeight +
        MANAGER_PDF_REFERENCE_LAYOUT.slots.slotsBottomPadding;
      const availableRowsHeight = remainingHeight - fixedHeight;
      const capacity = Math.max(
        1,
        Math.floor(
          availableRowsHeight /
            MANAGER_PDF_REFERENCE_LAYOUT.slots.rowHeight,
        ),
      );

      if (remainingHeight < managerGroupHeight(1) && page.length) {
        pushPage();
        continue;
      }

      const take = Math.min(capacity, sourceGroup.slots.length - offset);
      const group = {
        date: sourceGroup.date,
        slots: sourceGroup.slots.slice(offset, offset + take),
      };
      const height = managerGroupHeight(group.slots.length);

      if (page.length && usedHeight + height > maxHeight) {
        pushPage();
        continue;
      }

      page.push(group);
      usedHeight += height;
      offset += take;

      if (offset < sourceGroup.slots.length) pushPage();
    }
  });

  pushPage();
  return pages.length ? pages : [[]];
};

const managerDrawCheck = (context, left, top, size) => {
  context.save();
  context.fillStyle = MANAGER_PDF_COLORS.pine;
  context.beginPath();
  context.arc(left + size / 2, top + size / 2, size / 2, 0, Math.PI * 2);
  context.fill();

  context.strokeStyle = MANAGER_PDF_COLORS.white;
  context.lineWidth = 1.8;
  context.lineCap = "round";
  context.lineJoin = "round";
  context.beginPath();
  context.moveTo(left + 6.5, top + 11.2);
  context.lineTo(left + 10, top + 14.5);
  context.lineTo(left + 16, top + 7.5);
  context.stroke();
  context.restore();
};

const managerDrawBookingBadge = (context, details, allSlots) => {
  const badge = MANAGER_PDF_REFERENCE_LAYOUT.badge;

  context.save();
  context.shadowColor = "rgba(0, 0, 0, 0.18)";
  context.shadowBlur = 12;
  context.shadowOffsetY = 6;
  managerRoundedRectPath(
    context,
    badge.left,
    badge.top,
    badge.width,
    badge.height,
    14,
  );
  context.fillStyle = MANAGER_PDF_COLORS.white;
  context.fill();
  context.restore();

  context.save();
  managerRoundedRectPath(
    context,
    badge.left,
    badge.top,
    badge.width,
    badge.height,
    14,
  );
  context.fillStyle = MANAGER_PDF_COLORS.white;
  context.fill();
  context.strokeStyle = MANAGER_PDF_COLORS.line;
  context.lineWidth = 1.5;
  context.stroke();
  context.restore();

  managerDrawCheck(
    context,
    badge.checkLeft,
    badge.checkTop,
    badge.checkSize,
  );

  const centerX = badge.left + badge.width / 2;

  managerDrawText(context, String(allSlots.length || 1), {
    x: centerX,
    y: badge.countTop,
    family: MANAGER_PDF_FONTS.display,
    size: 36,
    weight: 700,
    color: MANAGER_PDF_COLORS.ink,
    align: "center",
  });

  managerDrawText(context, "Bookings", {
    x: centerX,
    y: badge.bookingsTop,
    size: 11.5,
    weight: 500,
    color: MANAGER_PDF_COLORS.inkSoft,
    align: "center",
  });

  managerDrawText(
    context,
    formatDateRange(allSlots) || "Confirmed",
    {
      x: centerX,
      y: badge.dateTop,
      size: 10.5,
      color: MANAGER_PDF_COLORS.muted,
      align: "center",
    },
  );

  context.save();
  context.strokeStyle = MANAGER_PDF_COLORS.line;
  context.lineWidth = 1;
  context.setLineDash([3, 3]);
  context.beginPath();
  context.moveTo(badge.left + 9, badge.referenceTop);
  context.lineTo(badge.left + badge.width - 9, badge.referenceTop);
  context.stroke();
  context.restore();

  managerDrawTrackedText(context, "REFERENCE NO", {
    x: centerX,
    y: badge.referenceLabelTop,
    size: 8,
    weight: 400,
    color: MANAGER_PDF_COLORS.muted,
    letterSpacing: 0.64,
    align: "center",
  });

  const reference = String(details.bookingReference || "-");
  let referenceSize = 11.5;
  let referenceWidth = 0;

  do {
    context.save();
    managerSetCanvasFont(context, {
      family: MANAGER_PDF_FONTS.mono,
      size: referenceSize,
      weight: 700,
    });
    referenceWidth = context.measureText(reference).width;
    context.restore();

    if (referenceWidth + 12 <= badge.width - 18) break;
    referenceSize -= 0.5;
  } while (referenceSize >= 8);

  const referencePillWidth = Math.min(
    badge.width - 18,
    referenceWidth + 12,
  );
  const referencePillLeft = centerX - referencePillWidth / 2;

  context.save();
  managerRoundedRectPath(
    context,
    referencePillLeft,
    badge.referenceValueTop,
    referencePillWidth,
    badge.referenceValueHeight,
    6,
  );
  context.fillStyle = MANAGER_PDF_COLORS.pineTint;
  context.fill();
  context.strokeStyle = "#C9E4D5";
  context.lineWidth = 1;
  context.stroke();
  context.restore();

  managerDrawText(context, reference, {
    x: centerX,
    y: badge.referenceValueTop + 4,
    family: MANAGER_PDF_FONTS.mono,
    size: referenceSize,
    weight: 700,
    color: MANAGER_PDF_COLORS.pineDeep,
    align: "center",
  });
};

const managerDrawSlotGroups = (context, groups) => {
  const layout = MANAGER_PDF_REFERENCE_LAYOUT.slots;
  let groupTop = MANAGER_PDF_REFERENCE_LAYOUT.main.bookingsTop;

  groups.forEach((group, groupIndex) => {
    const headTop = groupTop;
    const trackHeight = 42;
    const dateBoxTop = headTop + 10 + (trackHeight - layout.dateBoxSize) / 2;
    const dateInfoLeft = layout.listLeft + layout.dateColumnWidth + layout.columnGap;

    context.save();
    managerRoundedRectPath(
      context,
      layout.listLeft,
      dateBoxTop,
      layout.dateBoxSize,
      layout.dateBoxSize,
      layout.dateBoxRadius,
    );
    context.fillStyle = MANAGER_PDF_COLORS.pineTint;
    context.fill();
    context.restore();

    managerDrawText(context, formatDay(group.date), {
      x: layout.listLeft + layout.dateBoxSize / 2,
      y: dateBoxTop + 7,
      family: MANAGER_PDF_FONTS.display,
      size: 15,
      weight: 700,
      color: MANAGER_PDF_COLORS.ink,
      align: "center",
    });

    managerDrawTrackedText(
      context,
      formatMonth(group.date).toUpperCase(),
      {
        x: layout.listLeft + layout.dateBoxSize / 2,
        y: dateBoxTop + 25.5,
        size: 8,
        color: MANAGER_PDF_COLORS.ink,
        letterSpacing: 0.32,
        align: "center",
      },
    );

    managerDrawText(context, formatDate(group.date), {
      x: dateInfoLeft,
      y: headTop + 21,
      size: 13,
      weight: 750,
      color: MANAGER_PDF_COLORS.ink,
    });

    managerDrawText(
      context,
      `${group.slots.length} ${group.slots.length === 1 ? "slot" : "slots"}`,
      {
        x: layout.listLeft + layout.listWidth,
        y: headTop + 22,
        size: 10,
        weight: 650,
        color: "#78847D",
        align: "right",
      },
    );

    const slotsLeft = layout.listLeft + layout.slotsLeftOffset;
    const slotsWidth = layout.listWidth - layout.slotsLeftOffset;
    let rowTop = headTop + layout.groupHeaderHeight;

    group.slots.forEach((slot, slotIndex) => {
      const time = `${formatTime(slot.start)} - ${formatTime(slot.end)}`;
      const price = formatMoney(slot.currency, slot.price);
      const timeLineHeight = 13.2;
      const priceLineHeight = 12;
      const isLastRow = slotIndex === group.slots.length - 1;
      const rowHeight = isLastRow
        ? layout.lastRowHeight
        : layout.rowHeight;

      managerDrawText(context, time, {
        x: slotsLeft,
        y: rowTop + (rowHeight - timeLineHeight) / 2,
        size: layout.timeFontSize,
        weight: layout.timeFontWeight,
        color: "#667269",
      });

      if (price) {
        managerDrawText(context, price, {
          x: slotsLeft + slotsWidth,
          y: rowTop + (rowHeight - priceLineHeight) / 2,
          size: layout.priceFontSize,
          weight: layout.priceFontWeight,
          color: MANAGER_PDF_COLORS.ink,
          align: "right",
        });
      }

      if (slotIndex < group.slots.length - 1) {
        context.save();
        context.strokeStyle = MANAGER_PDF_COLORS.rowLine;
        context.lineWidth = 1;
        context.beginPath();
        context.moveTo(slotsLeft, rowTop + rowHeight - 0.5);
        context.lineTo(
          slotsLeft + slotsWidth,
          rowTop + rowHeight - 0.5,
        );
        context.stroke();
        context.restore();
      }

      rowTop += rowHeight;
    });

    const groupBottom = rowTop + layout.slotsBottomPadding;

    if (groupIndex < groups.length - 1) {
      context.save();
      context.strokeStyle = MANAGER_PDF_COLORS.groupLine;
      context.lineWidth = 1;
      context.beginPath();
      context.moveTo(
        layout.listLeft + layout.slotsLeftOffset,
        groupBottom - 0.5,
      );
      context.lineTo(
        layout.listLeft + layout.listWidth,
        groupBottom - 0.5,
      );
      context.stroke();
      context.restore();
    }

    groupTop = groupBottom;
  });
};

const managerDrawPolicyAndFooter = (context, details) => {
  const policy = MANAGER_PDF_REFERENCE_LAYOUT.policy;
  const cancellationPolicy = String(details.cancellationPolicy || "").trim();
  const supportEmail = String(details.supportEmail || "").trim();
  const supportPhone = String(details.supportPhone || "").trim();

  if (cancellationPolicy) {
    context.save();
    context.strokeStyle = MANAGER_PDF_COLORS.line;
    context.lineWidth = 1;
    context.beginPath();
    context.moveTo(1, policy.top);
    context.lineTo(619, policy.top);
    context.stroke();
    context.restore();

    const heading = "CANCELLATION POLICY";
    const headingSpacing = 0.95;
    const headingWidth = managerMeasureTrackedText(context, heading, {
      family: MANAGER_PDF_FONTS.mono,
      size: 9.5,
      weight: 600,
      letterSpacing: headingSpacing,
    });
    const dotRadius = 1.4;
    const dotGap = 6;
    const groupWidth = headingWidth + dotGap + dotRadius * 2;
    const groupLeft = 310 - groupWidth / 2;

    managerDrawTrackedText(context, heading, {
      x: groupLeft,
      y: policy.top + 9,
      family: MANAGER_PDF_FONTS.mono,
      size: 9.5,
      weight: 600,
      color: MANAGER_PDF_COLORS.muted,
      letterSpacing: headingSpacing,
    });

    context.save();
    context.fillStyle = MANAGER_PDF_COLORS.muted;
    context.beginPath();
    context.arc(
      groupLeft + headingWidth + dotGap + dotRadius,
      policy.top + 14,
      dotRadius,
      0,
      Math.PI * 2,
    );
    context.fill();
    context.restore();

    managerDrawTrackedText(context, "PLEASE NOTE", {
      x: policy.pleaseNoteLeft,
      y: policy.pleaseNoteTop,
      size: 10,
      color: MANAGER_PDF_COLORS.muted,
      letterSpacing: 0.8,
    });

    const basePolicyFontSize = 12.5;
    let policyFontSize = basePolicyFontSize;
    let policyLineHeight = 20;
    let policyLines = managerWrapCanvasText(context, cancellationPolicy, {
      maxWidth: policy.textWidth,
      family: MANAGER_PDF_FONTS.body,
      size: policyFontSize,
      weight: 400,
    });

    if (policyLines.length > 3) {
      policyFontSize = 11.5;
      policyLineHeight = 17.5;
      policyLines = managerWrapCanvasText(context, cancellationPolicy, {
        maxWidth: policy.textWidth,
        family: MANAGER_PDF_FONTS.body,
        size: policyFontSize,
        weight: 400,
      });
    }

    policyLines.slice(0, 4).forEach((line, index) => {
      managerDrawText(context, line, {
        x: policy.textLeft,
        y: policy.textTop + index * policyLineHeight,
        size: policyFontSize,
        color: MANAGER_PDF_COLORS.muted,
      });
    });
  }

  if (supportEmail || supportPhone) {
    const supportParts = [
      "If you need more information, contact us:",
      supportEmail,
      supportPhone
        ? `${supportEmail ? "or " : ""}phone ${supportPhone}`
        : "",
    ].filter(Boolean);
    const supportLine = supportParts.join(" ");

    managerDrawWrappedText(context, supportLine, {
      x: 310,
      y: policy.footerTop + 16,
      maxWidth: 538,
      maxLines: 2,
      lineHeight: 13,
      size: 10.5,
      color: MANAGER_PDF_COLORS.muted,
      align: "center",
    });
  }
};

const managerLoadOngroundLogoImage = () =>
  new Promise((resolve, reject) => {
    if (
      typeof window === "undefined" ||
      typeof window.Image !== "function"
    ) {
      reject(
        new Error(
          "Manager PDF Onground logo rendering requires browser image support.",
        ),
      );
      return;
    }

    const image = new window.Image();

    image.onload = () => {
      const width = Number(image.naturalWidth || image.width);
      const height = Number(image.naturalHeight || image.height);

      if (
        !Number.isFinite(width) ||
        !Number.isFinite(height) ||
        width <= 0 ||
        height <= 0
      ) {
        reject(
          new Error(
            "Onground logo loaded with invalid dimensions for the PDF.",
          ),
        );
        return;
      }

      resolve(image);
    };

    image.onerror = () => {
      reject(
        new Error(
          "Unable to load the Onground logo for the booking PDF.",
        ),
      );
    };

    image.src = resolveOngroundLogoForSurface(
      ONGROUND_LOGO_SURFACE.LIGHT,
    );
  });

const managerDrawReferencePage = (
  context,
  details,
  groups,
  pageIndex,
  pageCount,
  brandLogoImage,
) => {
  const page = MANAGER_PDF_REFERENCE_LAYOUT.page;
  const main = MANAGER_PDF_REFERENCE_LAYOUT.main;
  const header = MANAGER_PDF_REFERENCE_LAYOUT.header;
  const allSlots = normalizeSlots(details.slots);

  context.save();
  context.fillStyle = MANAGER_PDF_COLORS.white;
  context.fillRect(0, 0, page.cssWidth, page.cssHeight);
  context.restore();

  context.save();
  managerRoundedRectPath(
    context,
    0.5,
    0.5,
    page.cssWidth - 1,
    page.cssHeight - 1,
    18,
  );
  context.strokeStyle = MANAGER_PDF_COLORS.line;
  context.lineWidth = 1;
  context.stroke();
  context.restore();

  context.save();
  context.fillStyle = MANAGER_PDF_COLORS.perforation;
  for (let x = 14; x < page.cssWidth; x += 16) {
    context.beginPath();
    context.arc(x, 5, 2.6, 0, Math.PI * 2);
    context.fill();
  }
  context.restore();

  context.save();
  context.beginPath();
  context.arc(54, 50.625, 13, 0, Math.PI * 2);
  context.strokeStyle = MANAGER_PDF_COLORS.ink;
  context.lineWidth = 1.5;
  context.stroke();
  context.restore();

  context.save();
  managerSetCanvasFont(context, {
    family: MANAGER_PDF_FONTS.display,
    size: 12,
    weight: 700,
  });
  context.fillStyle = MANAGER_PDF_COLORS.ink;
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.fillText(
    String(details.venueName || "O").charAt(0).toUpperCase(),
    54,
    50.625,
  );
  context.restore();

  managerDrawText(context, details.venueName || "Onground venue", {
    x: header.venueNameLeft,
    y: header.venueNameTop,
    family: MANAGER_PDF_FONTS.display,
    size: 16,
    weight: 600,
    color: MANAGER_PDF_COLORS.ink,
  });

  managerDrawText(context, "SECURE BOOKING", {
    x: header.secureRight,
    y: header.secureTop,
    family: MANAGER_PDF_FONTS.display,
    size: 11,
    weight: 700,
    color: MANAGER_PDF_COLORS.goldDeep,
    align: "right",
  });

  const brandLogoNaturalWidth = Number(
    brandLogoImage?.naturalWidth || brandLogoImage?.width,
  );
  const brandLogoNaturalHeight = Number(
    brandLogoImage?.naturalHeight || brandLogoImage?.height,
  );

  if (
    !brandLogoImage ||
    !Number.isFinite(brandLogoNaturalWidth) ||
    !Number.isFinite(brandLogoNaturalHeight) ||
    brandLogoNaturalWidth <= 0 ||
    brandLogoNaturalHeight <= 0
  ) {
    throw new Error(
      "Onground logo is unavailable for manager PDF rendering.",
    );
  }

  const brandLogoScale = Math.min(
    header.brandLogoWidth / brandLogoNaturalWidth,
    header.brandLogoHeight / brandLogoNaturalHeight,
  );
  const brandLogoDrawWidth =
    brandLogoNaturalWidth * brandLogoScale;
  const brandLogoDrawHeight =
    brandLogoNaturalHeight * brandLogoScale;
  const brandLogoDrawLeft =
    header.brandLogoLeft +
    header.brandLogoWidth -
    brandLogoDrawWidth;
  const brandLogoDrawTop =
    header.brandLogoTop +
    (header.brandLogoHeight - brandLogoDrawHeight) / 2;

  managerDrawText(context, "BY", {
    x: brandLogoDrawLeft - 6,
    y: header.byOngroundTop,
    family: MANAGER_PDF_FONTS.display,
    size: 14,
    weight: 700,
    color: MANAGER_PDF_COLORS.ink,
    align: "right",
  });

  context.drawImage(
    brandLogoImage,
    brandLogoDrawLeft,
    brandLogoDrawTop,
    brandLogoDrawWidth,
    brandLogoDrawHeight,
  );

  context.save();
  context.fillStyle = MANAGER_PDF_COLORS.pine;
  context.beginPath();
  context.arc(44, main.kickerTop + 6.5, 3, 0, Math.PI * 2);
  context.fill();
  context.restore();

  managerDrawTrackedText(context, "BOOKING CONFIRMED", {
    x: 54,
    y: main.kickerTop,
    family: MANAGER_PDF_FONTS.mono,
    size: 10,
    weight: 500,
    color: MANAGER_PDF_COLORS.pineDeep,
    letterSpacing: 1.6,
  });

  if (pageIndex === 0) {
    managerDrawText(context, "Booking", {
      x: main.left,
      y: main.titleTop,
      family: MANAGER_PDF_FONTS.display,
      size: 30,
      weight: 600,
      color: MANAGER_PDF_COLORS.ink,
    });
    managerDrawText(context, "Confirmation", {
      x: main.left,
      y: main.titleTop + 34.5,
      family: MANAGER_PDF_FONTS.display,
      size: 30,
      weight: 600,
      color: MANAGER_PDF_COLORS.ink,
    });

    managerDrawText(
      context,
      "Your payment was verified and your booking is confirmed.",
      {
        x: main.left,
        y: main.confirmationTop,
        size: 13.5,
        color: MANAGER_PDF_COLORS.inkSoft,
      },
    );
  } else {
    managerDrawText(context, "Booking Confirmation", {
      x: main.left,
      y: main.titleTop,
      family: MANAGER_PDF_FONTS.display,
      size: 26,
      weight: 600,
      color: MANAGER_PDF_COLORS.ink,
    });
    managerDrawTrackedText(context, `PAGE ${pageIndex + 1} OF ${pageCount}`, {
      x: main.left,
      y: main.confirmationTop,
      family: MANAGER_PDF_FONTS.mono,
      size: 9,
      weight: 500,
      color: MANAGER_PDF_COLORS.muted,
      letterSpacing: 0.7,
    });
  }

  const venueLine = [details.groundName, details.venueName]
    .filter(Boolean)
    .join(" \u2014 ");

  managerDrawText(context, venueLine, {
    x: main.left,
    y: main.venueTop,
    family: MANAGER_PDF_FONTS.display,
    size: 16,
    weight: 600,
    color: MANAGER_PDF_COLORS.ink,
  });

  managerDrawWrappedText(context, details.venueAddress || "", {
    x: main.left,
    y: main.addressTop,
    maxWidth: main.width,
    maxLines: 2,
    lineHeight: 15,
    size: 12.5,
    color: MANAGER_PDF_COLORS.muted,
  });

  managerDrawBookingBadge(context, details, allSlots);

  if (groups.length) {
    managerDrawSlotGroups(context, groups);
  } else {
    managerDrawWrappedText(context, details.bookingWhen || "Confirmed", {
      x: main.left,
      y: main.bookingsTop,
      maxWidth: main.width,
      maxLines: 3,
      lineHeight: 18,
      size: 13,
      weight: 600,
      color: MANAGER_PDF_COLORS.ink,
    });
  }

  if (pageIndex === pageCount - 1) {
    managerDrawPolicyAndFooter(context, details);
  }
};

const managerWaitForPdfFonts = async () => {
  if (typeof document === "undefined" || !document.fonts) {
    throw new Error("Manager PDF rendering requires browser font support.");
  }

  await document.fonts.ready;
  const checks = await Promise.all([
    document.fonts.load('600 16px "Fraunces"'),
    document.fonts.load('700 15px "Fraunces"'),
    document.fonts.load('400 13px "Inter"'),
    document.fonts.load('600 13px "Inter"'),
    document.fonts.load('700 10px "Inter"'),
    document.fonts.load('600 10px "IBM Plex Mono"'),
  ]);

  if (checks.some((faces) => !faces?.length)) {
    throw new Error(
      "Manager PDF fonts (Fraunces / Inter / IBM Plex Mono) are unavailable.",
    );
  }
};

const managerRenderPdfPageJpeg = (
  details,
  groups,
  pageIndex,
  pageCount,
  brandLogoImage,
) => {
  if (typeof document === "undefined") return null;

  const page = MANAGER_PDF_REFERENCE_LAYOUT.page;
  const scale = 2.4;
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(page.cssWidth * scale);
  canvas.height = Math.round(page.cssHeight * scale);

  const context = canvas.getContext("2d");
  if (!context) return null;

  context.setTransform(scale, 0, 0, scale, 0, 0);
  managerDrawReferencePage(
    context,
    details,
    groups,
    pageIndex,
    pageCount,
    brandLogoImage,
  );

  const bytes = dataUrlToBytes(canvas.toDataURL("image/jpeg", 0.96));
  if (!bytes?.length) return null;

  return {
    bytes,
    width: canvas.width,
    height: canvas.height,
  };
};

const buildManagerImagePdf = (images) => {
  const pages = (images || []).filter((image) => image?.bytes?.length);
  if (!pages.length) return null;

  const pageCount = pages.length;
  const catalogId = 1;
  const pagesId = 2;
  const firstPageId = 3;
  const firstContentId = firstPageId + pageCount;
  const firstImageId = firstContentId + pageCount;
  const objectCount = firstImageId + pageCount - 1;
  const objects = new Array(objectCount).fill("");

  objects[catalogId - 1] =
    `<< /Type /Catalog /Pages ${pagesId} 0 R >>`;

  const pageIds = Array.from(
    { length: pageCount },
    (_, index) => firstPageId + index,
  );

  objects[pagesId - 1] =
    `<< /Type /Pages /Kids [${pageIds
      .map((id) => `${id} 0 R`)
      .join(" ")}] /Count ${pageCount} >>`;

  pages.forEach((image, index) => {
    const pageId = firstPageId + index;
    const contentId = firstContentId + index;
    const imageId = firstImageId + index;
    const imageName = `ManagerPage${index + 1}`;
    const stream = [
      "q",
      `${PDF_PAGE_WIDTH} 0 0 ${PDF_PAGE_HEIGHT} 0 0 cm`,
      `/${imageName} Do`,
      "Q",
      "",
    ].join("\n");

    objects[pageId - 1] =
      `<< /Type /Page /Parent ${pagesId} 0 R /MediaBox [0 0 ${PDF_PAGE_WIDTH} ${PDF_PAGE_HEIGHT}] /Resources << /XObject << /${imageName} ${imageId} 0 R >> >> /Contents ${contentId} 0 R >>`;
    objects[contentId - 1] =
      `<< /Length ${encode(stream).length} >>\nstream\n${stream}endstream`;

    const imageBytes =
      image.bytes instanceof Uint8Array
        ? image.bytes
        : Uint8Array.from(image.bytes);

    objects[imageId - 1] =
      `<< /Type /XObject /Subtype /Image /Width ${Math.round(
        Number(image.width),
      )} /Height ${Math.round(
        Number(image.height),
      )} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${
        imageBytes.length
      } >>\nstream\n${bytesToBinaryString(imageBytes)}\nendstream`;
  });

  let pdf = "%PDF-1.4\n% OnGround manager-reference booking confirmation\n";
  const offsets = [0];

  objects.forEach((object, index) => {
    offsets[index + 1] = encode(pdf).length;
    pdf += `${index + 1} 0 obj\n${object}\nendobj\n`;
  });

  const xrefOffset = encode(pdf).length;
  pdf += `xref\n0 ${objects.length + 1}\n`;
  pdf += "0000000000 65535 f \n";

  offsets.slice(1).forEach((offset) => {
    pdf += `${String(offset).padStart(10, "0")} 00000 n \n`;
  });

  pdf += [
    "trailer",
    `<< /Size ${objects.length + 1} /Root ${catalogId} 0 R >>`,
    "startxref",
    String(xrefOffset),
    "%%EOF",
    "",
  ].join("\n");

  return encode(pdf);
};

const buildManagerReferenceDownloadPdf = async (details) => {
  const [, brandLogoImage] = await Promise.all([
    managerWaitForPdfFonts(),
    managerLoadOngroundLogoImage(),
  ]);

  const normalizedSlots = normalizeSlots(details?.slots);
  const pages = managerPaginateSlotGroups(normalizedSlots);
  const pageCount = pages.length;
  const images = pages.map((groups, pageIndex) =>
    managerRenderPdfPageJpeg(
      details,
      groups,
      pageIndex,
      pageCount,
      brandLogoImage,
    ),
  );

  if (images.some((image) => !image?.bytes?.length)) {
    throw new Error("Unable to render the manager-reference PDF page.");
  }

  return buildManagerImagePdf(images);
};

export const downloadBookingConfirmationPdf = async (
  filename,
  details,
) => {
  const pdfBytes = await buildManagerReferenceDownloadPdf(details);

  if (!pdfBytes?.length) {
    throw new Error("Unable to build the booking confirmation PDF.");
  }

  const blob = new Blob([pdfBytes], {
    type: "application/pdf",
  });
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = objectUrl;
  link.download = filename;
  link.style.display = "none";

  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
};
